import { useMemo } from "react";

import { parseISO, startOfMonth, subMonths, subSeconds } from "date-fns";

import { useLocalizedDate } from "utils/date";

export function getStartTimeSixMonthsAgo(): string {
  const fiveMonthsAgo = subMonths(new Date(), 5);
  return new Date(Date.UTC(fiveMonthsAgo.getUTCFullYear(), fiveMonthsAgo.getUTCMonth(), 1)).toISOString();
}

export function getStartTimeCurrentMonth(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString();
}

export const getEndOfMonth = (isoString: string) => {
  const date = parseISO(isoString);
  return subSeconds(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1)), 1).toISOString();
};

/**
 * Fills in missing months with zero values for the last `monthCount` months
 * @param data - Array of data items with a month field
 * @param dataKeys - The keys to set to 0 for missing months (e.g., ['total_hep', 'total_rtm'])
 * @param monthCount - Number of months to include (default is 6)
 * @returns Array with all months filled in, sorted chronologically
 */
export function fillMissingMonths<T extends { month: string }>(
  data: T[],
  dataKeys: (keyof T)[] | keyof T,
  monthCount = 6
): T[] {
  const now = new Date();
  const months: string[] = [];

  for (let i = monthCount - 1; i >= 0; i -= 1) {
    const utcYear = now.getUTCFullYear();
    const utcMonth = now.getUTCMonth() - i;
    const monthStart = new Date(Date.UTC(utcYear, utcMonth, 1));
    months.push(monthStart.toISOString().split("T")[0]);
  }

  const dataMap = new Map<string, T>();
  data.forEach(item => {
    dataMap.set(item.month, item);
  });

  const keys = Array.isArray(dataKeys) ? dataKeys : [dataKeys];

  const filledData: T[] = months.map(month => {
    const existingData = dataMap.get(month);
    if (existingData) {
      return existingData;
    }

    const filledMonth: Record<string, unknown> = { month };
    keys.forEach(key => {
      filledMonth[key as string] = 0;
    });
    return filledMonth as T;
  });

  return filledData;
}

export const MONTH_COUNT = 12;

export const useMonthOptions = () => {
  const { format } = useLocalizedDate();

  const monthOptions = useMemo(() => {
    const now = startOfMonth(new Date());
    return Array.from({ length: MONTH_COUNT }, (_, i) => {
      const d = subMonths(now, i);
      return { label: format(d, "MMMM yyyy"), value: format(d, "yyyy-MM-dd") };
    });
  }, [format]);

  return { monthOptions };
};
